import { Button } from "@progress/kendo-react-buttons";
import React, { useState, useRef, useEffect } from "react";
import { FaSortDown } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import styles from "./ColorPicker.module.scss";

const ColorPicker = ({ value, onChange, name, ...props }) => {
  const handleChange = (newValue) => {
    if (onChange) {
      const formValue = newValue === "none" ? "none" : newValue;
      const syntheticEvent = {
        target: {
          name: name,
          value: formValue,
          type: "text",
        },
        currentTarget: {
          name: name,
          value: formValue,
        },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <ColorPickerComponent
      value={value}
      onChange={handleChange}
      name={name}
      {...props}
    />
  );
};

const ColorPickerComponent = ({
  initialColor = null,
  onColorChange,
  className = "",
  // Kendo Form compatibility props
  value,
  onChange,
  name,
}) => {
  const [selectedColor, setSelectedColor] = useState(
    value || initialColor || ""
  );
  const [showPicker, setShowPicker] = useState(false);
  const [customColor, setCustomColor] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (value !== undefined && value !== selectedColor) {
      setSelectedColor(value || "");
    }
  }, [value, selectedColor]);

  const colorPalette = [
    [
      "#79C7E8",
      "#E0FFDF",
      "#CDDEF7",
      "#007AD7",
      "#FFEBBF",
      "#FFF1DD",
      "#FFCED2",
      "#FECDFF",
      "#89C7E7",
      "#ADD8E5",
    ],
    [
      "#C4EBF1",
      "#FFFFC2",
      "#FFF684",
      "#F9DEEA",
      "#FBF0EF",
      "#E5E5E5",
      "#DAC9E4",
      "#B0B2D4",
      "#FF9FBA",
      "#96FFED",
    ],
    [
      "#C4FF91",
      "#FFD99D",
      "#9DF5FF",
      "#B9FFE0",
      "#FBFBFB",
      "#F4DEDE",
      "#FEFFE8",
      "#BCD2BB",
      "#D1AEFF",
      "#B19CD9",
    ],
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closePicker();
      }
    };

    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPicker]);

  const openPicker = () => {
    setIsAnimating(true);
    setShowPicker(true);
    setTimeout(() => setIsAnimating(false), 200);
  };

  const closePicker = () => {
    setIsAnimating(true);
    setShowPicker(false);
    setTimeout(() => setIsAnimating(false), 200);
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);

    if (onChange) {
      onChange(color);
    }
    if (onColorChange) {
      onColorChange(color);
    }

    closePicker();
  };

  const handleCustomColorApply = () => {
    if (!customColor) return;
    setSelectedColor(customColor);

    if (onChange) {
      onChange(customColor);
    }
    if (onColorChange) {
      onColorChange(customColor);
    }

    setCustomColor("");
    closePicker();
  };

  const handleReset = () => {
    setSelectedColor("");

    if (onChange) {
      onChange("none");
    }
    if (onColorChange) {
      onColorChange("none");
    }

    closePicker();
  };

  const getButtonClasses = () => {
    const baseClasses = [styles.colorPickerButton];
    if (showPicker) {
      baseClasses.push(styles.active);
    } else {
      baseClasses.push(styles.inactive);
    }
    return baseClasses.join(" ");
  };

  const getDropdownClasses = () => {
    const baseClasses = [styles.colorPickerDropdown];
    if (isAnimating) {
      baseClasses.push(styles.animating);
    } else {
      baseClasses.push(styles.visible);
    }
    return baseClasses.join(" ");
  };

  const displayColor =
    selectedColor && selectedColor !== "none" ? selectedColor : "#ffffff";
  const hasSelectedColor =
    selectedColor && selectedColor.trim() !== "" && selectedColor !== "none";

  return (
    <div className={`${styles.colorPickerContainer} ${className}`}>
      <div
        className={`d-flex p-1 align-items-center justify-content-center border-1 gap-3 rounded-1  ${
          styles.colorPickerWrapper
        } ${showPicker ? styles.active : ""}`}
        onClick={showPicker ? undefined : openPicker}
        style={{ cursor: showPicker ? "default" : "pointer" }}
      >
        <div ref={dropdownRef}>
          <button
            type="button"
            className={getButtonClasses()}
            style={{
              backgroundColor: displayColor,
              border: "1px solid #dee2e6",
            }}
            onClick={openPicker}
            title={
              hasSelectedColor
                ? `Selected: ${selectedColor}`
                : "No color selected"
            }
          >
            {!hasSelectedColor && <div className={styles.resetIcon} />}
          </button>

          {showPicker && (
            <div className={getDropdownClasses()}>
              <div className="d-flex align-items-center justify-content-end mt-2 mx-2">
                <RxCross2
                  className={styles.closeButton}
                  role="button"
                  onClick={closePicker}
                />
              </div>

              <div className="p-3 ">
                <div className={styles.paletteGrid}>
                  {colorPalette.map((row, rowIndex) => (
                    <div key={rowIndex} className={styles.paletteRow}>
                      {row.map((color, colIndex) => (
                        <button
                          key={`${rowIndex}-${colIndex}`}
                          className={`${styles.paletteColor} ${
                            hasSelectedColor && selectedColor === color
                              ? styles.selected
                              : styles.unselected
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => handleColorSelect(color)}
                          title={color}
                        />
                      ))}
                    </div>
                  ))}

                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <div className={styles.customColorSection}>
                      <span>Custom Color:</span>
                      <input
                        type="color"
                        value={customColor}
                        onChange={(e) => setCustomColor(e.target.value)}
                        className={styles.customColorInput}
                      />
                    </div>
                    <div className={styles.resetButton} onClick={handleReset}>
                      <div className={styles.resetIcon} />
                    </div>
                  </div>
                </div>
              </div>

              {customColor && (
                <div className={styles.dropdownFooter}>
                  <div></div>
                  <Button
                    onClick={handleCustomColorApply}
                    className="common-btn button-small"
                    role="button"
                  >
                    Apply
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <FaSortDown
          className={` ${showPicker ? `${styles.rotatedArrow} mt-1` : "mb-1"}`}
        />
      </div>
    </div>
  );
};

export default ColorPicker;
